/**
 * The Add box's bin chips (D222): Auto lets the AI pick, the rest say which bin the line goes
 * to. A radio group: one Tab stop (the chosen chip), arrow keys move the choice, and a screen
 * reader says which is chosen. Auto is left out where there is no AI to pick, Context where the
 * server runs none.
 *
 * Alt+1 to Alt+5 pick the chips in the order shown (`event.code`, so a keyboard layout where Alt
 * changes the character still works), and say so on each chip. Only while the Add card has focus,
 * or nothing does: never from inside an open popup, or another field elsewhere on the page.
 */

import { useEffect, useRef } from "react";
import { ADD_BINS, availableBins, type AddBin } from "../addBin";

/** Whether an Alt+digit should reach the chips: focus is in the Add card (or nowhere), and no
 * popup is open over it. */
function shortcutApplies(card: Element | null): boolean {
  if (document.querySelector("dialog[open]")) return false;
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  return card?.contains(active) ?? false;
}

export function AddBinChips({
  bin,
  onChange,
  aiConfigured,
  serverAi,
}: {
  bin: AddBin;
  onChange: (bin: AddBin) => void;
  aiConfigured: boolean;
  serverAi: boolean;
}) {
  const offered = availableBins(aiConfigured, serverAi);
  const group = useRef<HTMLDivElement>(null);
  // Read through refs so the listener is added once, not on every render.
  const latest = useRef({ offered, onChange });
  latest.current = { offered, onChange };

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const match = /^Digit([1-9])$/.exec(event.code);
      const picked = match ? latest.current.offered[Number(match[1]) - 1] : undefined;
      if (!picked || !shortcutApplies(group.current?.closest(".panel") ?? null)) return;
      event.preventDefault();
      latest.current.onChange(picked);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /** Arrow keys move the choice round the group, as in any radio group, and focus follows it. */
  function onArrow(event: React.KeyboardEvent) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    const edge = event.key === "Home" ? 0 : event.key === "End" ? offered.length - 1 : null;
    if (!step && edge === null) return;
    event.preventDefault();
    const at = offered.indexOf(bin);
    const next = edge ?? (at + step + offered.length) % offered.length;
    onChange(offered[next]);
    group.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[next]?.focus();
  }

  return (
    <div
      ref={group}
      className="row wrap"
      role="radiogroup"
      aria-label="Add as"
      style={{ gap: "0.3rem" }}
      onKeyDown={onArrow}
    >
      {ADD_BINS.filter((entry) => offered.includes(entry.bin)).map((entry) => (
        <button
          key={entry.bin}
          type="button"
          role="radio"
          aria-checked={bin === entry.bin}
          tabIndex={bin === entry.bin ? 0 : -1}
          aria-keyshortcuts={`Alt+${offered.indexOf(entry.bin) + 1}`}
          title={`Alt+${offered.indexOf(entry.bin) + 1}`}
          className={`btn small ${bin === entry.bin ? "primary" : "ghost"}`}
          onClick={() => onChange(entry.bin)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}
